import { type LoaderFunctionArgs, type MetaFunction } from '@remix-run/node'
import { useLoaderData, useLocation } from '@remix-run/react'
import { useEffect, type ReactNode } from 'react'
import { EyebrowLabel } from '#app/components/eyebrow-label.tsx'
import { MarketingHero } from './components/marketing-hero.tsx'
import {
	parseWorkoutNotation,
	WorkoutNotationDemo,
} from './components/workout-notation-demo.tsx'

/**
 * The feature index. Section ids double as deep-link anchors — the login
 * tips in app/utils/momentum-tips.ts point at them, and a contract test
 * keeps the two lists in sync.
 */
export const REFERENCE_SECTIONS = [
	{ id: 'anatomy', title: 'The line' },
	{ id: 'volume', title: 'Sets & reps' },
	{ id: 'rep-lists', title: 'Rep lists & rest-pause' },
	{ id: 'time-distance', title: 'Time, distance & calories' },
	{ id: 'per-side', title: 'Unilateral work' },
	{ id: 'intensity', title: 'Load' },
	{ id: 'effort', title: 'Effort — RPE, RIR & rep max' },
	{ id: 'rate', title: 'Pace, speed & heart rate' },
	{ id: 'tempo', title: 'Tempo' },
	{ id: 'intervals', title: 'Rest & intervals' },
	{ id: 'progressions', title: 'Progressions' },
	{ id: 'rounds', title: 'Rounds & circuits' },
	{ id: 'section-styles', title: 'AMRAP, EMOM, For Time & ladders' },
	{ id: 'divisions', title: 'Divisions' },
	{ id: 'comments', title: 'Comments' },
	{ id: 'in-the-app', title: 'How Impulse reads it' },
	{ id: 'gotchas', title: 'Sharp edges' },
] as const

export const PLAYGROUND_EXAMPLES = {
	session: `~ Warm Up ~
Air Squat, 2x10 @bw

~ Strength ~
Squat, 5x3 @85%1rm rest 3:00
Bench Press, 5x[12,10,8,6,4] @[95,110,125,140,155]lb

~ Conditioning ~
3 Rounds, every 2:30
  KB Swing, 15 @53lb
  Push Up, 10 @bw
  Row, 250m`,
	singleLine: 'Bench Press, 3x5 @8rpe every 2:30',
	circuit: `4 Rounds,
  Row, 200m in 0:35
  Run, 2x100m rest 1:00`,
	ladder: `21-15-9,
  Thruster, @95lb
  Pull Up, @bw`,
} as const

export const loader = async (_args: LoaderFunctionArgs) => {
	return {
		examples: Object.fromEntries(
			Object.entries(PLAYGROUND_EXAMPLES).map(([key, notation]) => [
				key,
				{ notation, parsed: parseWorkoutNotation(notation) },
			]),
		),
	}
}

export const meta: MetaFunction = () => [
	{ title: 'Language Reference — Momentum' },
	{
		name: 'description',
		content:
			'The Momentum workout language on one page: a quick guide plus a detailed index of every feature, with examples.',
	},
]

// ---------------------------------------------------------------------------
// Local presentation helpers
// ---------------------------------------------------------------------------

function P({ children }: { children: ReactNode }) {
	return (
		<p className="mt-4 text-base leading-relaxed text-ink-fg-soft">
			{children}
		</p>
	)
}

function Code({ children }: { children: ReactNode }) {
	return (
		<code className="rounded-sm bg-ink-soft px-1.5 py-0.5 font-mono-display text-[0.85em] text-ink-fg">
			{children}
		</code>
	)
}

/** A block of example notation, optionally annotated line by line. */
function Ex({ children }: { children: string }) {
	return (
		<pre className="mt-4 overflow-x-auto rounded-md border border-m-rule bg-ink-soft px-4 py-3 font-mono-display text-sm leading-relaxed text-ink-fg">
			{children}
		</pre>
	)
}

/** A syntax-summary table: rows of [notation, meaning]. */
function SynTable({ rows }: { rows: Array<[string, string]> }) {
	return (
		<div className="mt-4 overflow-x-auto rounded-md border border-m-rule">
			<table className="w-full border-collapse text-sm">
				<thead>
					<tr className="bg-ink-soft/60 text-left">
						<th className="px-3 py-2 text-eyebrow text-ink-fg-soft">
							You write
						</th>
						<th className="px-3 py-2 text-eyebrow text-ink-fg-soft">
							It means
						</th>
					</tr>
				</thead>
				<tbody>
					{rows.map(([notation, meaning], i) => (
						<tr key={i} className={i % 2 === 1 ? 'bg-ember/[0.035]' : undefined}>
							<td className="whitespace-nowrap px-3 py-1.5 font-mono-display text-ink-fg">
								{notation}
							</td>
							<td className="px-3 py-1.5 leading-relaxed text-ink-fg-soft">
								{meaning}
							</td>
						</tr>
					))}
				</tbody>
			</table>
		</div>
	)
}

function Section({
	id,
	title,
	children,
}: {
	id: string
	title: string
	children: ReactNode
}) {
	return (
		<section id={id} className="mb-14 scroll-mt-24">
			<h3 className="font-display text-display-3">{title}</h3>
			{children}
		</section>
	)
}

// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------

export default function ReferenceRoute() {
	const { examples } = useLoaderData<typeof loader>()
	const { hash } = useLocation()

	// Deep links (#anatomy etc. — every login tip uses one) must land on
	// their section. The app scrolls inside a container that root.tsx resets
	// on navigation, which can swallow the browser's native anchor jump, so
	// re-run the jump after mount/layout.
	useEffect(() => {
		if (!hash) return
		const el = document.getElementById(hash.slice(1))
		if (!el) return
		const frame = requestAnimationFrame(() => {
			el.scrollIntoView({ block: 'start' })
		})
		return () => cancelAnimationFrame(frame)
	}, [hash])

	return (
		<>
			<MarketingHero
				eyebrow="Language reference"
				heading="The whole language, on one page."
				subhead="A quick guide to get you writing, then a detailed index of every feature. Bookmark it — every login tip links back here."
				ctaLabel=""
				variant="compact"
				decorations={['grid']}
			/>

			{/* ------------------------------------------------------------- */}
			{/* Quick guide                                                    */}
			{/* ------------------------------------------------------------- */}
			<section className="mx-auto w-full max-w-5xl px-6 py-16 md:px-10">
				<EyebrowLabel>Quick guide</EyebrowLabel>
				<h2 className="mt-5 font-display text-display-2">
					One line, one exercise.
				</h2>
				<P>
					A workout is plain text. Each line is an exercise; each blank line
					starts a new section. The parts of a line always appear in this
					order — only the name and the volume are required:
				</P>
				<Ex>{'Name, Volume @Load @Effort tempo Code rest Time'}</Ex>
				<SynTable
					rows={[
						['Squat, 3x5', '3 sets of 5 reps'],
						['Squat, 3x5 @185lb', 'at 185 pounds'],
						['Squat, 3x5 @80%1rm', 'at 80% of your 1-rep max'],
						['Bench Press, 3x5 @8rpe', 'at a perceived effort of 8/10'],
						['Deadlift, 5x5 rest 3:00', '3 minutes rest after each set'],
						['Push Up, 10x10 every 1:00', 'a set starts every minute (EMOM)'],
						['Row, 3x500m', 'distance instead of reps'],
						['Plank, 3x0:30', 'time instead of reps'],
						['Bench Press, 5x[12,10,8,6,4]', 'each set has its own reps'],
						['Squat, 5x5 @100++5lb', 'load climbs 5 lb per set'],
						['3 Rounds, + indented lines', 'a circuit of the lines below'],
						['~ Warm Up ~', 'names a division of the session'],
					]}
				/>
				<P>
					Everything is live below — edit the notation and watch the structure
					update.
				</P>
				<div className="mt-6">
					<WorkoutNotationDemo
						examples={examples}
						initial="session"
						caption="Playground"
						rows={10}
					/>
				</div>
			</section>

			{/* ------------------------------------------------------------- */}
			{/* Feature index                                                  */}
			{/* ------------------------------------------------------------- */}
			<section className="border-t border-m-rule bg-ink-soft/30">
				<div className="mx-auto w-full max-w-5xl px-6 py-16 md:px-10 md:py-20">
					<EyebrowLabel>Feature index</EyebrowLabel>
					<h2 className="mt-5 font-display text-display-2">
						Every feature, in detail.
					</h2>

					<div className="mt-10 grid gap-12 md:grid-cols-[15rem_1fr]">
						<aside className="md:sticky md:top-24 md:self-start">
							<EyebrowLabel>Contents</EyebrowLabel>
							<ul className="mt-6 space-y-2 text-sm">
								{REFERENCE_SECTIONS.map((s) => (
									<li key={s.id}>
										<a
											href={`#${s.id}`}
											className="text-ink-fg-soft hover:text-ink-fg"
										>
											{s.title}
										</a>
									</li>
								))}
							</ul>
						</aside>

						<article className="min-w-0 max-w-[68ch]">
							<Section id="anatomy" title="The line">
								<P>
									Everything before the first comma is the exercise name;
									everything after it describes the work. Names can contain
									apostrophes, ampersands, hyphens, parentheses, and digits —{' '}
									<Code>Farmer's Carry</Code>, <Code>Clean &amp; Jerk</Code>,{' '}
									<Code>Squat (Paused)</Code>, <Code>21s Curl</Code> — because
									only the comma ends the name.
								</P>
								<Ex>{`Push Up, 20
Bench Press, 5x[12,10,8,6,4] @[95,110,125,140,155]lb @8rpe tempo 30X1 //3:00`}</Ex>
								<P>
									The parts of a line come in a fixed order: volume, then{' '}
									<Code>@</Code> intensities, then <Code>tempo</Code>, then the
									rest interval. A line like{' '}
									<Code>Squat, 3x5 rest 2:00 tempo 30X1</Code> won't parse —
									tempo belongs before the interval.
								</P>
								<P>
									A few lowercase unit words (<Code>kg</Code>, <Code>lb</Code>,{' '}
									<Code>rm</Code>, <Code>rpe</Code>, <Code>rir</Code>) can't
									appear as standalone words in a name — capitalize them (
									<Code>KB Swing</Code>, <Code>KG Swing</Code>) and they're
									fine. Periods, slashes, and plus signs aren't name characters.
								</P>
							</Section>

							<Section id="volume" title="Sets & reps">
								<SynTable
									rows={[
										['20', 'one set of 20 reps'],
										['3x10', '3 sets of 10 reps'],
										['3x8-12', '3 sets of 8–12 reps'],
										['3xAMAP', '3 sets of as many reps as possible'],
										['3X5', 'x is case-insensitive'],
									]}
								/>
								<P>
									Sets default to 1 when omitted. The <Code>x</Code> hugs its
									numbers — <Code>3 x 5</Code> with spaces won't parse. Rep
									counts must be whole numbers but may be zero — <Code>3x0</Code>{' '}
									records a round you got none of, while <Code>3x2.5</Code> is
									rejected. Set counts must be positive; <Code>0x5</Code> is
									rejected. <Code>AMAP</Code> ("as many
									as possible") is the max-effort rep count — note that in
									volume position it's <Code>AMAP</Code>, not{' '}
									<Code>AMRAP</Code> (that's a section header — see below).
								</P>
							</Section>

							<Section id="rep-lists" title="Rep lists & rest-pause">
								<P>
									A bracket list after <Code>x</Code> gives each set its own
									rep count. Inside a list, <Code>8+4</Code> is rest-pause: 8
									reps, a few breaths, then 4 more to finish the set.
								</P>
								<Ex>{`Bench Press, 5x[12,10,8,6,4]
Leg Press, 3x[12,12,8+4]`}</Ex>
								<P>
									Lists work in most value positions — reps, loads, efforts,
									rests, even distances with the unit outside the bracket:{' '}
									<Code>Row, [500,400,300]m</Code>.
								</P>
							</Section>

							<Section id="time-distance" title="Time, distance & calories">
								<SynTable
									rows={[
										['Row, 500m', 'distance: m, km, mi, yd, ft'],
										['Plank, 0:30', 'duration in minutes:seconds'],
										['Plank, 3x90s', 'seconds, minutes (min), hours (h)'],
										['Bike, 3x15cal', 'calories'],
										['Row, 3x250m in 1:00', 'each 250m raced against 1:00'],
										['Run, 2mi in 14:00', 'a time cap on the whole distance'],
									]}
								/>
								<P>
									Volume isn't limited to reps — any distance, duration, or
									calorie count works, with or without a set count.{' '}
									<Code>in</Code> attaches a time budget to the work itself
									(the space after <Code>in</Code> is required). Durations
									accept tenths: <Code>1:55.7</Code>.
								</P>
							</Section>

							<Section id="per-side" title="Unilateral work">
								<Ex>{`Split Squat, 3x10/side
Lunge, 3x10/leg
DB Press, 3x10/arm`}</Ex>
								<P>
									A per-side marker means "per side" — exactly one of{' '}
									<Code>/side</Code>, <Code>/leg</Code>, or <Code>/arm</Code>,
									directly after the volume.
								</P>
							</Section>

							<Section id="intensity" title="Load">
								<SynTable
									rows={[
										['@185lb', 'pounds — @185 bare also means lb'],
										['@62.5kg', 'kilograms (also g, and pd for pood)'],
										['@bw', 'bodyweight'],
										['@1.5bw', '1.5× bodyweight'],
										['@50%bw', 'half bodyweight'],
										['@135-155lb', 'a load range'],
										['@[95,110,125,140,155]lb', 'per-set loads'],
										['@[35lb,16kg,53lb]', 'lists can mix units'],
										['@24inch', 'any word works as an equipment unit'],
										['@2orange', 'colors are units too (bands, plates)'],
									]}
								/>
								<P>
									<Code>@</Code> attaches the load. Unit keywords are
									lowercase — <Code>@100LB</Code> parses, but the parser treats
									"LB" as a custom equipment unit rather than pounds. Distance
									and duration targets can be intensities as well:{' '}
									<Code>Run, 4x400m @800m</Code> or{' '}
									<Code>Row, 3x250m @0:45</Code>.
								</P>
							</Section>

							<Section id="effort" title="Effort — RPE, RIR & rep max">
								<SynTable
									rows={[
										['@8rpe', 'rate of perceived exertion, 1–10 (8.5 works)'],
										['@8-9rpe', 'an RPE range'],
										['@2rir', 'two reps in reserve'],
										['@3rm', 'your 3-rep max'],
										['@80%1rm', '80% of your 1-rep max'],
										['@90%5RM', '90% of your 5-rep max'],
										['@[7.5,8,8.5]rpe', 'per-set efforts'],
									]}
								/>
								<P>
									Always anchor a percentage to the rep max it's a percentage{' '}
									<em>of</em>: <Code>@70%1rm</Code>, <Code>@90%5RM</Code>. A
									bare percent like <Code>@70%</Code> doesn't say 70% of what —
									if you mean 70% effort, that's <Code>@7rpe</Code>; if you
									mean load, write <Code>@70%1rm</Code>. (The parser accepts
									the bare form and currently reads it as <Code>%1rm</Code>,
									so be explicit about which one you intend.)
								</P>
								<P>
									A line takes one load and, optionally, one effort:{' '}
									<Code>Squat, 3x5 @225lb @8rpe</Code> prescribes the load and
									the effort it should feel like. A time target can ride along
									too: <Code>Deadlift, 1x3 @155lb @8rpe in 0:20</Code>. More
									than one load or more than one effort on the same line won't
									parse.
								</P>
							</Section>

							<Section id="rate" title="Pace, speed & heart rate">
								<SynTable
									rows={[
										['@6:00/mi', 'pace (also /km, /mile, /kilometer)'],
										['@18mph', 'speed (also kph, km/h)'],
										['@150bpm', 'heart rate'],
										['@80%hrmax', 'percent of max heart rate'],
										['@[6:00,5:45,5:30]/mi', 'per-set paces'],
									]}
								/>
								<P>
									For cardio, the intensity is a rate. Ramp it per set with a
									list, or hold it steady for the whole piece.
								</P>
							</Section>

							<Section id="tempo" title="Tempo">
								<Ex>{`Squat, 3x5 @135lb tempo 30X1 rest 2:00
Bench Press, 3x8 tempo 3-1-3`}</Ex>
								<P>
									Four digits — eccentric, pause, concentric, pause — where{' '}
									<Code>X</Code> means explosive: <Code>30X1</Code> is 3
									seconds down, no pause, explode up, 1-second squeeze. The
									hyphenated <Code>3-1-3</Code> form works too. The keyword is
									lowercase <Code>tempo</Code>, it sits after the intensities
									and before the interval, and the code starts with a digit.
								</P>
							</Section>

							<Section id="intervals" title="Rest & intervals">
								<SynTable
									rows={[
										['rest 2:00', 'clock starts when the set ends'],
										['rest 90', 'bare numbers are seconds (90s works too)'],
										['/0:30', 'shorthand for rest 0:30'],
										['every 1:00', 'clock includes the work — EMOM-style'],
										['//3:00', 'shorthand for every 3:00'],
										['//:30', 'colon shorthand for every 0:30'],
										['rest [2:00,2:30,3:00]', 'per-set rests'],
										['every 400m', 'distance-based intervals'],
									]}
								/>
								<P>
									The words choose the semantics: <Code>rest</Code> and{' '}
									<Code>/</Code> are exclusive (rest starts after the work),{' '}
									<Code>every</Code> and <Code>//</Code> are inclusive (the
									next set starts on the mark, work included). A line with no
									interval simply has no prescribed clock.
								</P>
							</Section>

							<Section id="progressions" title="Progressions">
								<SynTable
									rows={[
										['5x5++1', 'reps climb by 1 per set: 5,6,7,8,9'],
										['10x10--1', 'reps fall by 1 per set'],
										['@100++5lb', 'load climbs 5 lb per set'],
										['rest 2:00--0:15', 'rest shrinks 15s per set'],
										['4x400m++200m', 'distance grows per set'],
										['5x0:30++0:10', 'duration grows per set'],
										['5x5**2', 'multiply per set: 5,10,20,40,80'],
										['5x24\\\\2', 'divide per set: 24,12,6,3,…'],
									]}
								/>
								<P>
									<Code>++</Code> and <Code>--</Code> write a ramp without
									listing every step; <Code>**</Code> and <Code>\\</Code>{' '}
									multiply and divide. Progressions are type-matched — numbers
									step by numbers, durations by durations, distances by
									distances.
								</P>
							</Section>

							<Section id="rounds" title="Rounds & circuits">
								<P>
									A <Code>3 Rounds,</Code> header loops through the indented
									lines beneath it. The header can carry its own clock —{' '}
									<Code>rest 2:00</Code> between rounds, or{' '}
									<Code>every 2:30</Code> to start each round on the mark. (The
									trailing comma on headers is optional.)
								</P>
								<Ex>{`3 Rounds, rest 2:00
  Squat, 5 @135lb
  Push Up, 10 @bw`}</Ex>
								<P>
									A one-line superset separates exercises with semicolons:{' '}
									<Code>3 rounds, Push Up, 10 @bw; Bench Press, 5 @5rm</Code>.
								</P>
								<P>
									Inside a rounds section, a bare bracket list distributes{' '}
									<em>per round</em> rather than per set, and nesting goes a
									level deeper when each round has its own sets:
								</P>
								<Ex>{`10 Rounds,
  Push Up, [2,4,6,8,10,12,14,16,18,20] @bw

4 rounds,
  Bench Press, [3,2,1]x[10,8,6] @[135,145,155]lb rest [2:00,3:00,4:00]`}</Ex>
								<P>
									Short intensity lists pad with their last value. Exercises
									themselves can rotate round by round: <Code>..N</Code> holds
									an entry for N rounds, <Code>%%N</Code> strides, and{' '}
									<Code>*[…]</Code> loops the list.
								</P>
								<Ex>{`6 rounds,
  [Squat..2, Lunge..2, Dip..2], 10 @bw
  *[Push Up, Dip], 10 @bw`}</Ex>
							</Section>

							<Section
								id="section-styles"
								title="AMRAP, EMOM, For Time & ladders"
							>
								<Ex>{`12:00 AMRAP,
  Push Up, 10 @bw
  Air Squat, 15 @bw

10:00 EMOM,
  Push Up, 10 @bw

For Time,
  Burpee, 50 @bw

21-15-9,
  Thruster, @95lb
  Pull Up, @bw`}</Ex>
								<P>
									<Code>12:00 AMRAP,</Code> — as many rounds as possible in the
									time; bare minutes (<Code>12 AMRAP,</Code>) work too.{' '}
									<Code>10:00 EMOM,</Code> — one round every minute on the
									minute; whole minutes only. <Code>For Time,</Code> — one
									round, done as fast as possible.
								</P>
								<P>
									A rep-scheme ladder like <Code>21-15-9,</Code> needs at least
									three counts. Under it, lines can drop their volume — the
									scheme supplies the reps — and explicit-volume lines like{' '}
									<Code>Run, 200m</Code> mix in unchanged.
								</P>
							</Section>

							<Section id="divisions" title="Divisions">
								<Ex>{`~ Warm Up ~
Air Squat, 10 @bw

~ Main Effort ~
Deadlift, 5x5 @155lb rest 1:30`}</Ex>
								<P>
									A <Code>~ Name ~</Code> line on its own names everything that
									follows, until the next marker. An empty marker (
									<Code>~ ~</Code>) closes the current division without opening
									a new one. Division names allow letters, digits, spaces, and{' '}
									<Code>&amp; - _ +</Code>.
								</P>
							</Section>

							<Section id="comments" title="Comments">
								<Ex>{`# felt strong today
Bench Press, 3x5 @135lb # paused last rep`}</Ex>
								<P>
									Everything from <Code>#</Code> to the end of the line is
									stripped before parsing — whole-line or trailing, they never
									affect the plan.
								</P>
							</Section>

							<Section id="in-the-app" title="How Impulse reads it">
								<P>
									The language describes the work; the app resolves it against
									you. These behaviors sit on top of the notation itself.
								</P>
								<P>
									<strong>Exercise names are free text.</strong> Write any name
									you like — when you save a workout with a name the library
									doesn't know, Impulse asks you to link it to an existing
									exercise (as an alias), rename it, create it, or skip.
									Linking is what powers autocomplete, muscle attribution,
									bodyweight factors, and exercise history, so it's worth the
									click. Renaming an exercise later rewrites it in your saved
									notation.
								</P>
								<P>
									<strong>Percentages estimate effort, not load.</strong>{' '}
									<Code>@85%1rm</Code> doesn't look up your max and compute a
									weight — the app converts percentage prescriptions into an
									effort estimate (an RPE) when it summarizes and charts your
									training. <Code>@90%5RM</Code> is first re-anchored to its
									1RM equivalent.
								</P>
								<P>
									<strong>Bodyweight resolves against you.</strong>{' '}
									<Code>@bw</Code> uses your most recent logged body weight,
									and exercises carry a bodyweight load factor (a push up loads
									roughly 0.64× bodyweight) so bodyweight work counts toward
									tonnage honestly. If a workout needs your bodyweight and
									doesn't have it, Impulse prompts you when you finish.
								</P>
								<P>
									<strong>
										Results need an absolute anchor.
									</strong>{' '}
									Feel-based intensities — <Code>rpe</Code>, <Code>rir</Code>,{' '}
									<Code>rm</Code>, <Code>%1rm</Code> — are prescription
									vocabulary. When you log what actually happened, pair them
									with something concrete: a weight, a distance, a duration, or{' '}
									<Code>@bw</Code>. <Code>5 @8rpe</Code> can't be a result;{' '}
									<Code>5 @225lb @8rpe</Code> can.
								</P>
								<P>
									<strong>The editor is on your side.</strong> It highlights
									the parts of each line, completes exercise names from your
									own library along with grammar suggestions, explains what it
									expected when a line doesn't parse yet, and marks half-typed
									lines as "keep typing" rather than errors. And when you
									perform a workout, the app writes notation back for you —
									logged times splice in as <Code>in 0:33</Code> and measured
									rests as <Code>rest 2:37</Code>, so your results read in the
									same language as your plans.
								</P>
							</Section>

							<Section id="gotchas" title="Sharp edges">
								<SynTable
									rows={[
										['3 x 5', 'spaces around x break it — write 3x5'],
										['in1:00', 'the space after in is required'],
										['3x2.5', 'rep counts must be whole numbers (0 is fine)'],
										['0x5', 'set counts must be positive'],
										['@100kgs', 'rejected with a hint — the unit is kg'],
										['10:30 EMOM,', 'EMOM takes whole minutes only'],
										['Tempo 30X1', 'the keyword is lowercase tempo'],
										['3xAMRAP', 'in volume position it spells AMAP'],
										['21-15,', 'a ladder needs at least three counts'],
										[
											'Squat, @135lb',
											'volume-less lines only work under a ladder header',
										],
									]}
								/>
								<P>
									The editor flags all of these as you type, with a hint about
									what it expected next.
								</P>
							</Section>
						</article>
					</div>
				</div>
			</section>
		</>
	)
}
